// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/selectors/myActionItems.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/selectors/myActionItems.js
 *
 * The one "what do I owe" aggregation (ACEA-01, design authority
 * docs/adaptive-communication-engine-rev4.md §1.4 = A). The ◎ badge counts
 * this list and the Action Centre renders it, so the two cannot disagree:
 * badge === listMyActionItems(...).length, by construction.
 *
 * Pure: (state, session, users, extras) -> items[]. Nothing is fetched here.
 * Async inputs are passed in through `extras`:
 *   - extras.obligationTasks  Tier 1 obligation-derived tasks (useAppState)
 *   - extras.bugUpdates       the viewer's bug reports whose status moved and
 *                             that they have not marked as seen
 *   - extras.commQuestions    the viewer's own comm-questions views (ACEA-05):
 *                             'assignee' rows are Tier 2 tasks in the one task
 *                             list; an 'asker' row whose answer came back and
 *                             has no feedback yet is the in-app delivery
 *   - extras.workflowSteps    the viewer's open workflow steps (PDWB-02,
 *                             useMyWorkflowSteps) — Tier 2 tasks in the one list
 *   - extras.haltReviews      the viewer's open reviews of halted recurring
 *                             workflows (PDW-HALT-2, useMyHaltReviews) — Tier 2
 *                             tasks in the one list, every cause (WF-B-011)
 *   - extras.riskAiAssessments  risk-ai-assess `list` rows (RAI-05,
 *                             useRiskAiAssessmentList) — the undecided ones are
 *                             "decide on AI assessment" Tier 2 tasks
 *   - extras.now              Date used for "overdue" (defaults to now)
 *
 * COMM-B-011: every item is work the viewing person owes. Nothing here ranks,
 * compares or reports on anybody else, and the list is only ever built for the
 * session it is handed.
 *
 * EMP-B-003: an employee's own self-service portal items (portal reads, portal
 * training, role acknowledgements made in EmployeeHub) are never aggregated
 * here.
 *
 * Each item carries enough for ACEA-02 to order it without re-deriving:
 *   id          stable, unique across the list
 *   kind        source kind (see KINDS)
 *   category    triage category — one of CATEGORY_ORDER
 *   bucket      'overdue' | 'decision' | 'ontrack' (today's triage columns)
 *   subject     { module, id, version } — the record the work is about
 *   dueDate     ISO date/datetime or null
 *   mandatory   inputs for a mandatory flag: { decisionOwed, regulatory }
 *   criticality inputs for criticality: { severity } (record's own value, or null)
 *   nav         { kind: 'route', path } | { kind: 'modal', modal: { type, data } }
 *               | { kind: 'gap', gapId } | { kind: 'bug-ack', bugId }
 *   record      the underlying record (read-only; for display)
 *   detail      source-specific display facts (counts, regime, …)
 */

const { listControlOwnerAlerts } = require('../controlOwnerStatus.cjs');
const { isAwaitingOwnerAction, isAwaitingReviewerAction, isAwaitingPublish } = require('../documents/documentWorkflow.cjs');
const {
  resolveReaders,
  hasReaderAcknowledged,
  requiredAckVersion,
} = require('../compliance/resolveReaders.cjs');
const { withoutSampleRecords, realEmployees } = require('../sampleRecords.cjs');
const { detectRequiredWorkflows } = require('../ai/aiWorkflowTriggers.cjs');
const { listGapQuestionAssignments, listOpenTasksForUser, collectTaskSources, isOpenTaskStatus, riskAiAssessmentTasks, commQuestionTaskViews } = require('../tasks/assignments.cjs');
const { listReclassificationsAwaitingUser } = require('../incident/incidentReclassification.cjs');
const { listClosedIncidentsWithMissedNotification } = require('../incident/incidentMissedNotifications.cjs');
const { biaReviewsDueFor, biaReviewStatus } = require('../biaReviews/biaReviews.cjs');
const { biaOwnerIsUser } = require('../biaReviews/biaAuthority.cjs');

const CATEGORY_ORDER = [
  'Risk',
  'Incident',
  'Documents',
  'Gap Assessment',
  'Vendors & Access',
  'BIA',
  'Tasks & Findings',
];

/**
 * Every item kind this list can produce, with what is fixed per kind:
 *
 *   category          the triage category (CATEGORY_ORDER)
 *   decisionOwed      COMM-B-009 "a decision is assigned to you"
 *   regulatory        COMM-B-009 "it has a legal deadline"
 *   instanceDecision  decisionOwed is set per item, from the record, never
 *                     from this table (a question asked about a mandatory item)
 *   reads             what the kind is derived from: tenant state keys, plus
 *                     '@<extra>' for an input passed in through `extras`
 *                     ('@users' is the users argument). A list built where
 *                     one of these could not be read must leave the kind out
 *                     rather than read the gap as "nothing owed" (MNSA-01).
 *
 * classifyMandatory (myPriorityOrder.cjs) turns the flags into the mandatory
 * class. This table is the one place they are set: listMyActionItems builds
 * every item's category and mandatory flags from it, and the server's notice
 * routing reads the same entries (communications/noticeRouting.js). A kind
 * missing here throws — it never defaults to "not mandatory".
 */
const RISK_READS = ['risks', 'riskObligations', 'adhocTasks'];
const TASKS = 'Tasks & Findings';
const ITEM_KINDS = Object.freeze({
  'risk-review': { category: 'Risk', decisionOwed: true, reads: RISK_READS },
  'risk-approval': { category: 'Risk', decisionOwed: true, reads: RISK_READS },
  'risk-make-ready': { category: 'Risk', decisionOwed: true, reads: RISK_READS },
  'risk-reassessment': { category: 'Risk', decisionOwed: true, reads: RISK_READS },
  'risk-proposal': { category: 'Risk', decisionOwed: true, reads: ['riskProposals'] },
  'incident-proposal': { category: 'Incident', decisionOwed: true, reads: ['incidentProposals'] },
  'incident-reclassification': {
    category: 'Incident',
    decisionOwed: true,
    regulatory: true,
    reads: ['incidents'],
  },
  'incident-missed-notification': { category: 'Incident', regulatory: true, reads: ['incidents'] },
  'finding-proposal': {
    category: TASKS,
    decisionOwed: true,
    // A proposal a workflow step of the viewer's decides is listed as the step.
    reads: ['findingProposals', '@workflowSteps'],
  },
  'gap-questions': { category: 'Gap Assessment', reads: ['gapAnalyses'] },
  'uar-campaign': {
    category: 'Vendors & Access',
    decisionOwed: true,
    reads: ['uarCampaigns', 'employees'],
  },
  'vendor-not-responding': { category: 'Vendors & Access', decisionOwed: true, reads: ['vendors'] },
  'vendor-archive-proposal': {
    category: 'Vendors & Access',
    decisionOwed: true,
    reads: ['vendors'],
  },
  'ai-governance': {
    category: TASKS,
    regulatory: true,
    reads: ['aiSystems', 'aiProhibitedScreenings', 'dpias', 'tias'],
  },
  'control-owner-alert': {
    category: TASKS,
    reads: ['controlOwnership', 'soaData', 'employees', 'tenantDomains', '@users'],
  },
  'portal-answer-waiting': { category: TASKS, decisionOwed: true, reads: ['portalSubmissions'] },
  'portal-answer-sent-back': { category: TASKS, reads: ['portalSubmissions'] },
  'employee-doc-ack-gap': { category: 'Documents', reads: ['documents', 'employees'] },
  'doc-review': { category: 'Documents', decisionOwed: true, reads: ['documents'] },
  'doc-approval': { category: 'Documents', decisionOwed: true, reads: ['documents'] },
  'doc-publish': { category: 'Documents', reads: ['documents'] },
  'soa-approval': {
    category: 'Documents',
    decisionOwed: true,
    reads: ['soaVersions', 'soaSettings'],
  },
  'doc-read': { category: 'Documents', reads: ['documents', 'employees'] },
  'bia-input': { category: 'BIA', reads: ['biaAssessments'] },
  'bia-review': { category: 'BIA', decisionOwed: true, reads: ['biaAssessments'] },
  'bia-review-due': {
    category: 'BIA',
    reads: ['biaAssessments', 'biaReviews', 'biaReviewCycles', 'employees'],
  },
  'bug-update': { category: TASKS, reads: ['@bugUpdates'] },
  'comm-question-answer': { category: TASKS, reads: ['@commQuestions'] },
  finding: { category: TASKS, reads: ['findings'] },
  'finding-acceptance-unlinked': {
    category: TASKS,
    decisionOwed: true,
    reads: ['findings', 'risks'],
  },
  // Tasks — the one task list (Tier 1/2/3). The kind is the task's source.
  task: { category: TASKS, reads: ['adhocTasks'] },
  obligation: { category: TASKS, reads: ['@obligationTasks'] },
  'risk-action': { category: TASKS, reads: ['risks'] },
  'evidence-request-action': { category: TASKS, reads: ['evidenceRequests'] },
  'management-review-action': { category: TASKS, reads: ['managementReviews'] },
  'management-review-verification': {
    category: TASKS,
    reads: ['managementReviews', 'artefacts'],
  },
  'comm-question': { category: TASKS, instanceDecision: true, reads: ['@commQuestions'] },
  'workflow-step': { category: TASKS, reads: ['@workflowSteps'] },
  // PDW-HALT-2: a halted recurring workflow's review — a decision assigned to
  // the viewer (Retire or Accept, with a reason), so a decision owed.
  'workflow-halt-review': { category: TASKS, decisionOwed: true, reads: ['@haltReviews'] },
  // RAI-05: an undecided AI risk assessment — a decision owed (draft risk or
  // dismissal). Its decision is derived against the register, so a degraded
  // risk store makes it unknown, never "owed" (co-owner, RAI-05).
  'risk-ai-assessment': { category: 'Risk', decisionOwed: true, reads: ['@riskAiAssessments', '@users', 'risks'] },
});

/**
 * The mandatory inputs an item of `kind` carries — { decisionOwed, regulatory }.
 * `instanceDecisionOwed` is read only for a kind whose flag is per item.
 * listMyActionItems builds every item's flags with this, and the server's
 * notice routing classifies a notice with it (MNSA-01).
 */
function mandatoryFlagsFor(kind, instanceDecisionOwed = false) {
  const spec = itemKindSpec(kind);
  return {
    decisionOwed: spec.instanceDecision ? instanceDecisionOwed === true : spec.decisionOwed === true,
    regulatory: spec.regulatory === true,
  };
}

/** The ITEM_KINDS entry for a kind; throws for a kind this list does not produce. */
function itemKindSpec(kind) {
  const spec = Object.prototype.hasOwnProperty.call(ITEM_KINDS, kind) ? ITEM_KINDS[kind] : null;
  if (!spec) throw new Error(`myActionItems: unknown item kind "${kind}"`);
  return spec;
}

// Legacy terminal statuses set by the old handleApprove / handleResolve handlers.
// These handlers mutate risk.status but never clear risk.state, so a risk
// completed via the old flow can end up with risk.state stuck at an
// intermediate governance state. Guard here so stale Action Centre entries
// never surface for already-resolved risks.
// Removal blocked on backfill completion — see PR #<BACKFILL_PR>.
const TERMINAL_LEGACY_STATUSES = new Set([
  'approved',
  'resolved',
  'accepted',
  'transferred',
  'retired',
]);

/**
 * Decide which governance bucket a risk falls into for a given user.
 * Returns one of: 'review' | 'approval' | 'make-ready' | 'reassessment' | null.
 */
function classifyRiskForUser(risk, userId, isAdmin, obligationAssigneeRiskIds) {
  if (!risk) return null;
  const state = risk.state;
  const legacyStatus = risk.status;

  // If the risk was closed via the legacy flow, never surface it as actionable
  // even if risk.state is stuck at an intermediate governance state.
  if (TERMINAL_LEGACY_STATUSES.has(legacyStatus)) return null;
  const iAmReviewer =
    risk.reviewerId === userId ||
    (Array.isArray(risk.additionalReviewerIds) && risk.additionalReviewerIds.includes(userId));
  const iAmApprover = risk.approverId === userId;

  // Governance: use state when present
  if (state) {
    if (state === 'in-review' && iAmReviewer) return 'review';
    if (state === 'in-approval' && iAmApprover) return 'approval';
    if (state === 'make-ready' && iAmApprover) return 'make-ready';
    if (state === 'reassessment-required') {
      if (isAdmin) return 'reassessment';
      if (iAmReviewer || iAmApprover) return 'reassessment';
      if (obligationAssigneeRiskIds.has(risk.id)) return 'reassessment';
    }
    return null;
  }

  // Legacy fallback — only fires for risks not yet migrated.
  // Removal blocked on backfill completion — see PR #<BACKFILL_PR>.
  if (legacyStatus === 'in-analysis' && iAmReviewer) return 'review';
  if (legacyStatus === 'pending-approval' && iAmApprover) return 'approval';
  return null;
}

/**
 * Vendors that are not responding to their reassessment — admin-only.
 * A vendor qualifies when the reassessment engine has fired the 'overdue-7'
 * stage for the CURRENT cycle (ledger entry whose dueDate === the vendor's
 * live nextAssessmentDate) and the vendor is still actively tracked. Once the
 * vendor answers, the engine advances nextAssessmentDate so the old overdue
 * entry no longer matches and the item clears.
 */
function listVendorsNotResponding(state, isAdmin) {
  if (!isAdmin || !state) return [];
  return (state.vendors || []).filter((v) => {
    if (!v || v.archivedAt) return false;
    if (v.status === 'inactive' || v.status === 'offboarded') return false;
    const due = v.nextAssessmentDate;
    if (!due) return false;
    return (v.reassessmentEscalations || []).some(
      (e) => e && e.stage === 'overdue-7' && e.dueDate === due
    );
  });
}

/**
 * Vendors the reassessment engine has proposed for archive — admin-only.
 * The engine raises vendor.pendingArchiveProposal (status 'pending') once a
 * vendor stays unresponsive past the archive-proposal cadence stage; it NEVER
 * archives or changes vendor.status (VDR-B-006). The admin confirms the archive
 * or dismisses the proposal from the vendor detail view.
 */
function listVendorsPendingArchiveProposal(state, isAdmin) {
  if (!isAdmin || !state) return [];
  return (state.vendors || []).filter(
    (v) =>
      v &&
      !v.archivedAt &&
      v.pendingArchiveProposal &&
      v.pendingArchiveProposal.status === 'pending'
  );
}

// msr 4.3 / 4.4 / 4.7: an ad-hoc task of these types with nobody on it is
// owed by every administrator until someone owns it, so it cannot sit unseen.
// control-test-finding-debt is always the admins' (a finding the product could
// not raise); metric-criteria-change is theirs while the metric has no active owner.
const UNASSIGNED_ADMIN_TASK_TYPES = new Set([
  'context-change-response',
  'objective-review',
  'control-test-finding-debt',
  'metric-criteria-change',
  // RDRC-04: a leaver's account Entra reports enabled — a security event an
  // admin must explain; the sync creates it unassigned.
  'departed-account-enabled',
  // RCQA-04: a competence gap whose employee has no line manager who can
  // sign in — created unassigned, so admins own it until reassigned.
  'competence-gap',
]);

const DOMAIN_ACTION_SOURCES = new Set([
  'risk-action',
  'evidence-request-action',
  'management-review-action',
]);

function isAdminSession(session) {
  return session?.role === 'admin' || session?.role === 'super-admin';
}

function isPast(date, now) {
  return !!date && new Date(date) < now;
}

function uarSubjects(campaign, employees) {
  return employees.filter((e) => {
    if (e.status === 'departed' || e.status === 'inactive') return false;
    const s = campaign.scope || {};
    if (s.type === 'department') return e.department === s.value;
    if (s.type === 'employment-type') return e.employmentType === s.value;
    if (s.type === 'selected') return Array.isArray(s.value) && s.value.includes(e.id);
    return true;
  });
}

function taskNav(task) {
  if (task._source === 'comm-question') return { type: 'comm-question', data: task };
  // PDWB-02: a workflow step's task is its command surface (design §3b.3).
  if (task._source === 'workflow-step') return { type: 'workflow-step', data: task };
  // PDW-HALT-2: decided in its own modal (Retire / Accept with a reason).
  if (task._source === 'workflow-halt-review') return { type: 'workflow-halt-review', data: task };
  // RAI-05: decided in its own modal (create a draft risk / dismiss), never by status.
  if (task._source === 'risk-ai-assessment') return { type: 'risk-ai-assess', data: { assessmentId: task.assessmentId } };
  if (task.obligationId) return { type: 'obligation-task-detail', data: task };
  if (task._source === 'management-review-verification')
    return { type: 'mr-modal', data: task.managementReview };
  if (DOMAIN_ACTION_SOURCES.has(task._source)) return { type: 'domain-action-detail', data: task };
  return { type: 'adhoc-task-detail', data: task };
}

function taskSubjectModule(task) {
  if (task._source === 'obligation') return 'obligations';
  if (task._source === 'risk-action') return 'risks';
  if (task._source === 'evidence-request-action') return 'audit';
  if (task._source === 'management-review-action') return 'managementReview';
  if (task._source === 'management-review-verification') return 'managementReview';
  if (task._source === 'comm-question') return 'communications';
  if (task._source === 'workflow-step') return 'workflows';
  if (task._source === 'workflow-halt-review') return 'workflows';
  if (task._source === 'risk-ai-assessment') return 'risks';
  return 'tasks';
}

/**
 * The open tasks owed by this viewer: the shared task list (Tier 1/2/3,
 * assignee or owner) plus, for an administrator, the unassigned ad-hoc task
 * types every administrator owes. A task is in exactly one of the two sets —
 * the second requires nobody on it, the first requires the viewer on it.
 */
function listMyTasks(state, userId, isAdmin, obligationTasks, commQuestionTasks, workflowSteps, riskAiTasks, haltReviews) {
  const steps = Array.isArray(workflowSteps) ? workflowSteps : [];
  const aiTasks = Array.isArray(riskAiTasks) ? riskAiTasks : [];
  const reviews = Array.isArray(haltReviews) ? haltReviews : [];
  const mine = listOpenTasksForUser(state, userId, obligationTasks, commQuestionTasks, steps, aiTasks, reviews);
  if (!isAdmin) return mine;
  // An undecided AI assessment whose author is no longer a current user names
  // nobody: every admin owes it (riskAiAssessmentTasks).
  const unownedAi = collectTaskSources({}, [], [], [], aiTasks.filter((t) => t && !t.assigneeId))
    .filter((t) => isOpenTaskStatus(t.status))
    .map((t) => ({ ...t, _unassigned: true }));
  // An unassigned workflow step (its role has no holder) belongs to every
  // admin (design §3b.5) — the server returns those to an admin only.
  const unownedSteps = collectTaskSources(
    {},
    [],
    steps.filter((s) => s && !s.assigneeId)
  )
    .filter((t) => isOpenTaskStatus(t.status))
    .map((t) => ({ ...t, _unassigned: true }));
  // An unassigned halt review (its owner role has no holder) belongs to every
  // admin (design §2 Owner) — the server returns those to an admin only.
  const unownedReviews = collectTaskSources(
    {},
    [],
    [],
    [],
    [],
    reviews.filter((r) => r && !r.assigneeId)
  )
    .filter((t) => isOpenTaskStatus(t.status))
    .map((t) => ({ ...t, _unassigned: true }));
  const unowned = (state.adhocTasks || [])
    .filter(
      (t) =>
        t &&
        !t.assigneeId &&
        !t.ownerId &&
        UNASSIGNED_ADMIN_TASK_TYPES.has(t.type) &&
        isOpenTaskStatus(t.status)
    )
    .map((t) => ({ ...t, _source: 'adhoc', _unassigned: true }));
  return [...mine, ...unownedSteps, ...unownedReviews, ...unownedAi, ...unowned];
}

function sortTasks(tasks, now) {
  return tasks.sort((a, b) => {
    const aOd = isPast(a.dueDate, now) ? 0 : 1;
    const bOd = isPast(b.dueDate, now) ? 0 : 1;
    if (aOd !== bOd) return aOd - bOd;
    return (a.dueDate || '') < (b.dueDate || '') ? -1 : 1;
  });
}

const FINDING_STATUS_ORDER = { open: 0, 'in-remediation': 1, 'pending-verification': 2 };

/**
 * @param {object} state      tenant state
 * @param {object} session    { userId, role, ... } — the viewer
 * @param {Array}  users      tenant users (control-owner resolution)
 * @param {object} [extras]   { obligationTasks, bugUpdates, commQuestions, workflowSteps, haltReviews, now }
 * @returns {Array<object>}   the viewer's action items, in today's list order
 */
function listMyActionItems(rawState, session, users = [], extras = {}) {
  const userId = session?.userId;
  if (!rawState || !userId) return [];
  // No work item is ever raised for, or about, a sample person (GH #7870).
  const state = withoutSampleRecords(rawState);
  const isAdmin = isAdminSession(session);
  const now = extras.now instanceof Date ? extras.now : new Date(extras.now ?? Date.now());
  const employees = state.employees || [];
  const documents = state.documents || [];
  // Folded into every document's readers until RDRA-04's migration (DOC-B-026).
  const documentAssignments = state.documentAssignments || [];
  const out = [];
  // Category and the mandatory inputs come from ITEM_KINDS — the one per-kind
  // table the server reads too (MNSA-01). An item states its own decisionOwed
  // only where its kind says the flag is per item.
  const push = (item) => {
    const spec = itemKindSpec(item.kind);
    if (item.mandatory && !spec.instanceDecision) {
      throw new Error(`listMyActionItems: kind "${item.kind}" has no per-item mandatory flag`);
    }
    out.push({
      subject: null,
      dueDate: null,
      criticality: { severity: null },
      detail: {},
      ...item,
      category: spec.category,
      mandatory: mandatoryFlagsFor(item.kind, item.mandatory?.decisionOwed),
    });
  };

  // ── Risk governance ──
  const obligationAssigneeRiskIds = new Set(
    (state.riskObligations || [])
      .filter((o) => o && o.active !== false && o.assigneeId === userId)
      .map((o) => o.riskId)
  );
  const riskBuckets = { review: [], approval: [], 'make-ready': [], reassessment: [] };
  for (const r of state.risks || []) {
    const bucket = classifyRiskForUser(r, userId, isAdmin, obligationAssigneeRiskIds);
    if (bucket) riskBuckets[bucket].push(r);
  }
  const riskSubject = (r) => ({ module: 'risks', id: r.id, version: r.currentDecisionId || null });
  const riskRoute = (r) => ({ kind: 'route', path: `/risks/${r.id}/lifecycle` });

  for (const r of riskBuckets.review) {
    push({
      id: `risk-review-${r.id}`,
      kind: 'risk-review',
      bucket: 'decision',
      subject: riskSubject(r),
      nav: riskRoute(r),
      record: r,
    });
  }
  for (const r of riskBuckets.approval) {
    push({
      id: `risk-approval-${r.id}`,
      kind: 'risk-approval',
      bucket: 'decision',
      subject: riskSubject(r),
      nav: r.state ? riskRoute(r) : { kind: 'modal', modal: { type: 'risk-detail', data: r } },
      record: r,
    });
  }
  for (const r of riskBuckets['make-ready']) {
    const obligationCount = (state.riskObligations || []).filter(
      (o) =>
        o && o.active !== false && o.riskId === r.id && o.linkedDecisionId === r.currentDecisionId
    ).length;
    const openTaskCount = (state.adhocTasks || []).filter(
      (t) =>
        t &&
        t.linkedEntityType === 'risk' &&
        t.linkedEntityId === r.id &&
        t.linkedDecisionId === r.currentDecisionId &&
        t.status !== 'closed' &&
        t.status !== 'completed'
    ).length;
    push({
      id: `risk-makeready-${r.id}`,
      kind: 'risk-make-ready',
      bucket: 'decision',
      subject: riskSubject(r),
      nav: riskRoute(r),
      record: r,
      detail: { obligationCount, openTaskCount },
    });
  }
  for (const r of riskBuckets.reassessment) {
    push({
      id: `risk-reassess-${r.id}`,
      kind: 'risk-reassessment',
      bucket: 'overdue',
      subject: riskSubject(r),
      nav: riskRoute(r),
      record: r,
    });
  }

  // ── Proposals and incidents (admin triage, named approvers) ──
  // A risk proposal assigned to someone (LDGA-04: a leader-dialogue concern
  // handed to the ISMS owner) is that person's decision too, admin or not.
  for (const p of (state.riskProposals || []).filter(
    (x) => x && x.status === 'proposed' && (isAdmin || (x.assigneeId && x.assigneeId === userId))
  )) {
    push({
      id: `risk-proposal-${p.id}`,
      kind: 'risk-proposal',
      bucket: 'decision',
      subject: { module: 'risks', id: p.id, version: null },
      nav: { kind: 'route', path: '/risks' },
      record: p,
    });
  }
  if (isAdmin) {
    for (const p of (state.incidentProposals || []).filter((x) => x && x.status === 'proposed')) {
      push({
        id: `incident-proposal-${p.id}`,
        kind: 'incident-proposal',
        bucket: 'decision',
        subject: { module: 'incidents', id: p.id, version: null },
        nav: { kind: 'route', path: '/incidents' },
        record: p,
      });
    }
  }
  for (const { incident, regime, request } of listReclassificationsAwaitingUser(state, userId)) {
    push({
      id: `incident-reclassification-${incident.id}-${regime}-${request.id}`,
      kind: 'incident-reclassification',
      bucket: 'decision',
      subject: { module: 'incidents', id: incident.id, version: null },
      nav: { kind: 'route', path: '/incidents' },
      record: incident,
      detail: { regime, request },
    });
  }
  for (const { incident, regime, reason } of listClosedIncidentsWithMissedNotification(
    state,
    isAdmin,
    now.getTime()
  )) {
    push({
      id: `incident-missed-notification-${incident.id}-${regime}`,
      kind: 'incident-missed-notification',
      bucket: 'decision',
      subject: { module: 'incidents', id: incident.id, version: null },
      nav: { kind: 'route', path: `/incidents?incident=${encodeURIComponent(incident.id)}` },
      record: incident,
      detail: { regime, reason },
    });
  }
  {
    // Admins decide every proposal; a proposal addressed to a named person
    // (ACEA-06: the communication owner, who may be an ISMS admin without the
    // tenant admin role) reaches that person too.
    // SCCA-03: a source-consistency proposal past its due date names the ISMS
    // manager as escalation owner on the SAME proposal — one item for both,
    // never a copy (docs/source-consistency-check-design.md §4.2).
    const mine = (x) =>
      isAdmin ||
      (x.assigneeId && x.assigneeId === userId) ||
      (x.escalationOwnerId && x.escalationOwnerId === userId);
    // A workflow run's proposal is decided from the step task that waits on it
    // (PDWB-02): when the viewer carries that step, the step is the item and
    // the proposal is not listed a second time.
    const decidedByMyStep = new Set(
      (extras.workflowSteps || [])
        .filter((s) => s && s.proposalId && isOpenTaskStatus(s.status))
        .filter((s) => s.assigneeId === userId || !s.assigneeId)
        .map((s) => s.proposalId)
    );
    for (const p of (state.findingProposals || []).filter(
      (x) => x && x.status === 'proposed' && mine(x) && !decidedByMyStep.has(x.id)
    )) {
      push({
        id: `finding-proposal-${p.id}`,
        kind: 'finding-proposal',
        // ACEA-02: was the legacy 'Findings', which is in no CATEGORY_ORDER
        // filter, so a finding proposal was reachable only under "All".
        bucket: 'decision',
        subject: { module: 'findings', id: p.id, version: null },
        nav: { kind: 'route', path: '/findings' },
        record: p,
      });
    }
  }

  // ── Gap assessment ──
  for (const a of listGapQuestionAssignments(state, userId)) {
    push({
      id: `gapq-${a.id}`,
      kind: 'gap-questions',
      bucket: isPast(a.dueDate, now) ? 'overdue' : 'decision',
      subject: { module: 'gap', id: a.nav.gapId, version: null },
      dueDate: a.dueDate || null,
      nav: { kind: 'gap', gapId: a.nav.gapId },
      record: a,
      detail: { count: a.count },
    });
  }

  // ── Vendors & access ──
  for (const campaign of state.uarCampaigns || []) {
    if (campaign.status !== 'active') continue;
    if (!(campaign.reviewers || []).some((r) => r.userId === userId)) continue;
    const subjects = uarSubjects(campaign, employees);
    const decidedIds = new Set(
      (campaign.decisions || [])
        .filter((d) => d.reviewerId === userId && d.decision !== null)
        .map((d) => d.employeeId)
    );
    const pending = subjects.filter((e) => !decidedIds.has(e.id)).length;
    if (pending === 0) continue;
    push({
      id: `uar-${campaign.id}`,
      kind: 'uar-campaign',
      bucket: isPast(campaign.deadline, now) ? 'overdue' : 'decision',
      subject: { module: 'employees', id: campaign.id, version: null },
      dueDate: campaign.deadline || null,
      nav: { kind: 'route', path: '/employees' },
      record: campaign,
      detail: { pending, total: subjects.length },
    });
  }
  for (const v of listVendorsNotResponding(state, isAdmin)) {
    push({
      id: `vendor-notresp-${v.id}`,
      kind: 'vendor-not-responding',
      bucket: 'overdue',
      subject: { module: 'vendors', id: v.id, version: null },
      dueDate: v.nextAssessmentDate || null,
      nav: { kind: 'modal', modal: { type: 'vendor-detail', data: v } },
      record: v,
    });
  }
  for (const v of listVendorsPendingArchiveProposal(state, isAdmin)) {
    push({
      id: `vendor-archive-${v.id}`,
      kind: 'vendor-archive-proposal',
      bucket: 'overdue',
      subject: { module: 'vendors', id: v.id, version: null },
      nav: { kind: 'modal', modal: { type: 'vendor-detail', data: v } },
      record: v,
    });
  }

  // ── AI governance, control ownership, control-owner answers (admin) ──
  if (isAdmin) {
    const ctx = {
      screenings: state.aiProhibitedScreenings || [],
      dpias: state.dpias || [],
      tias: state.tias || [],
    };
    for (const sys of state.aiSystems || []) {
      if (!sys || sys.status === 'blocked') continue; // refused for use: nothing left to screen (AIGOV-B-002)
      const flags = detectRequiredWorkflows(sys, null, ctx);
      if (flags.length === 0) continue;
      push({
        id: `ai-gov-${sys.id}`,
        kind: 'ai-governance',
        bucket: 'decision',
        subject: { module: 'aiGovernance', id: sys.id, version: null },
        nav: { kind: 'route', path: '/ai-governance' },
        record: sys,
        detail: { flags },
      });
    }
    // GH #6959 — controls whose owner has left or cannot be resolved to one
    // current person. Derived, so it is correct for every departure route.
    for (const alert of listControlOwnerAlerts(state, users)) {
      push({
        id: `control-owner-alert-${alert.controlId}`,
        kind: 'control-owner-alert',
        bucket: 'decision',
        subject: { module: 'controls', id: alert.controlId, version: null },
        nav: { kind: 'route', path: `/controls?open=${encodeURIComponent(alert.controlId)}` },
        record: alert,
      });
    }
    // COP-B-007 — the reviewer's queue of control-owner answers, never the
    // owner's own self-service list (EMP-B-003).
    for (const sub of state.portalSubmissions || []) {
      if (!sub || (sub.status !== 'waiting' && sub.status !== 'changes-requested')) continue;
      push({
        id: `portal-answer-${sub.id}`,
        kind: sub.status === 'waiting' ? 'portal-answer-waiting' : 'portal-answer-sent-back',
        bucket: 'decision',
        subject: { module: 'controls', id: sub.id, version: null },
        nav: { kind: 'route', path: '/controls?tab=portal-answers' },
        record: sub,
      });
    }
  }

  // ── Documents ──
  if (isAdmin) {
    // A document with at least one current required reader who has not
    // acknowledged the version in force — the one list and the one ack rule
    // (DOC-B-026), so this agrees with Gate 3 and the Documents screens. The
    // resolver excludes departed / offboarding people itself, and a sample
    // named as a specific reader stays required (GH #7870), so the list it is
    // given must keep realEmployees()' record of removed samples.
    const readerPool = realEmployees(employees);
    const inactiveIds = new Set(
      readerPool.filter((e) => e.status === 'departed' || e.status === 'offboarding').map((e) => e.id)
    );
    for (const doc of documents) {
      if (doc.documentStatus !== 'published') continue;
      const required = resolveReaders(doc, readerPool, documentAssignments);
      if (required.length === 0) continue;
      // A legacy `requiredReaders` id resolves unfiltered; one naming a departed
      // or offboarding employee is not raised here (they are never owed a read).
      const unacked = required.filter(
        (id) => !inactiveIds.has(id) && !hasReaderAcknowledged(id, doc, readerPool)
      );
      if (unacked.length === 0) continue;
      push({
        id: `doc-ackgap-${doc.id}`,
        kind: 'employee-doc-ack-gap',
        bucket: 'decision',
        subject: { module: 'documents', id: doc.id, version: doc.version ?? null },
        nav: { kind: 'route', path: '/employees' },
        record: doc,
        detail: { unackedCount: unacked.length, totalRequired: required.length },
      });
    }
  }
  const docSubject = (d) => ({ module: 'documents', id: d.id, version: d.version ?? null });
  const docHub = (d) => ({ kind: 'modal', modal: { type: 'document-workflow-hub', data: d } });
  for (const d of documents) {
    if (!isAwaitingReviewerAction(d, userId)) continue;
    push({
      id: `doc-review-${d.id}`,
      kind: 'doc-review',
      bucket: 'decision',
      subject: docSubject(d),
      nav: docHub(d),
      record: d,
    });
  }
  for (const d of documents) {
    if (!isAwaitingOwnerAction(d) || (d.ownerId || d.owner) !== userId) continue;
    push({
      id: `doc-approval-${d.id}`,
      kind: 'doc-approval',
      bucket: 'decision',
      subject: docSubject(d),
      nav: docHub(d),
      record: d,
    });
  }
  for (const d of documents) {
    if (!isAwaitingPublish(d, userId)) continue;
    push({
      id: `doc-publish-${d.id}`,
      kind: 'doc-publish',
      bucket: 'decision',
      subject: docSubject(d),
      nav: docHub(d),
      record: d,
    });
  }
  // SoA versions awaiting approval: the designated framework approver, or any
  // administrator when no approver is configured for that framework.
  const soaSettings = state.soaSettings || {};
  for (const [fwId, versions] of Object.entries(state.soaVersions || {})) {
    if (!Array.isArray(versions)) continue;
    const designated = soaSettings.frameworkApprovers?.[fwId];
    if (!(designated ? userId === designated : isAdmin)) continue;
    versions.forEach((v, idx) => {
      if (!v || (v.approvalStatus || 'approved') !== 'pending') return;
      push({
        id: `soa-approval-${fwId}-${v.id ?? v.version ?? idx}`,
        kind: 'soa-approval',
        bucket: 'decision',
        subject: { module: 'soa', id: fwId, version: v.version ?? null },
        nav: { kind: 'route', path: '/soa' },
        record: v,
        detail: { frameworkId: fwId },
      });
    });
  }
  for (const d of documents) {
    if (d.documentStatus !== 'published') continue;
    if (!resolveReaders(d, employees, documentAssignments).includes(userId)) continue;
    if (hasReaderAcknowledged(userId, d, employees)) continue;
    push({
      id: `doc-read-${d.id}`,
      kind: 'doc-read',
      bucket: 'ontrack',
      subject: { module: 'documents', id: d.id, version: requiredAckVersion(d) },
      nav: { kind: 'modal', modal: { type: 'document-review', data: d } },
      record: d,
    });
  }

  // ── BIA ──
  for (const bia of state.biaAssessments || []) {
    if (bia.status === 'completed' || bia.status === 'archived') continue;
    for (const sh of bia.stakeholders || []) {
      if (sh.userId !== userId || sh.status === 'completed') continue;
      push({
        id: `bia-input-${bia.id}-${sh.id}`,
        kind: 'bia-input',
        bucket: isPast(sh.dueDate, now) ? 'overdue' : 'decision',
        subject: { module: 'bcdr', id: bia.id, version: null },
        dueDate: sh.dueDate || null,
        nav: { kind: 'modal', modal: { type: 'bia-detail', data: bia } },
        record: bia,
        detail: { stakeholder: sh },
      });
    }
  }
  for (const bia of state.biaAssessments || []) {
    if (bia.owner !== userId || bia.status !== 'in-review') continue;
    push({
      id: `bia-review-${bia.id}`,
      kind: 'bia-review',
      bucket: 'decision',
      subject: { module: 'bcdr', id: bia.id, version: null },
      nav: { kind: 'modal', modal: { type: 'bia-detail', data: bia } },
      record: bia,
    });
  }
  // msr 4.5a (BCR-03): completed BIAs past their review cycle — the owner's
  // to review (an unowned one, an admin's). The same rule as the BIA view.
  const biaReviewsDue = biaReviewsDueFor(
    state,
    userId,
    isAdmin,
    (owner, uid) => biaOwnerIsUser(owner, uid, employees),
    now.getTime()
  );
  for (const bia of biaReviewsDue) {
    const st = biaReviewStatus(state, bia, now.getTime());
    push({
      id: `bia-review-due-${bia.id}`,
      kind: 'bia-review-due',
      bucket: 'overdue',
      subject: { module: 'bcdr', id: bia.id, version: null },
      dueDate: st.dueAt || null,
      nav: { kind: 'modal', modal: { type: 'bia-detail', data: bia } },
      record: bia,
      detail: { reviewStatus: st },
    });
  }

  // ── Bug report status updates (passed in; never fetched here) ──
  for (const bug of extras.bugUpdates || []) {
    push({
      id: `bug-${bug.id}`,
      kind: 'bug-update',
      bucket: 'ontrack',
      subject: { module: 'bugs', id: bug.id, version: bug.status || null },
      nav: { kind: 'bug-ack', bugId: bug.id },
      record: bug,
    });
  }

  // ── Answers to the viewer's own questions (ACEA-05, rev 4 §5.2 step 5) ──
  // The in-app delivery: stays until the viewer says whether it helped.
  for (const q of extras.commQuestions || []) {
    if (!q || q.role !== 'asker' || q.status !== 'completed' || q.feedback) continue;
    push({
      id: `comm-answer-${q.id}`,
      kind: 'comm-question-answer',
      bucket: 'ontrack',
      subject: { module: 'communications', id: q.id, version: null },
      nav: { kind: 'modal', modal: { type: 'comm-question', data: q } },
      record: q,
    });
  }

  // ── Findings ──
  // An 'accepted-risk' finding is not open work: its acceptance is carried by
  // the linked risk, which the risk sweeper returns to reassessment
  // (FIND-B-007). It is listed below only when NO risk carries it.
  const findings = (state.findings || [])
    .filter((f) => f.owner === userId && f.status !== 'closed' && f.status !== 'accepted-risk')
    .sort((a, b) => (FINDING_STATUS_ORDER[a.status] ?? 9) - (FINDING_STATUS_ORDER[b.status] ?? 9));
  for (const f of findings) {
    push({
      id: `finding-${f.id}`,
      kind: 'finding',
      bucket: isPast(f.correctiveActionDueDate, now) ? 'overdue' : 'decision',
      subject: { module: 'findings', id: f.id, version: null },
      dueDate: f.correctiveActionDueDate || null,
      criticality: { severity: f.severity ?? null },
      nav: { kind: 'route', path: '/findings' },
      record: f,
    });
  }

  // ── Accepted findings no risk carries (ACEA-02b, founder decision A) ──
  // An acceptance with no linked risk is ignored, not accepted (TRYGGR_INTENT
  // §11): nothing reviews it, and FIND-B-007 requires a risk that exists in the
  // register. Records accepted before that gate, or whose linked risk was
  // since deleted, are owed a decision by the finding's owner: link the
  // acceptance to a risk, or reopen the finding. A link is counted only when it
  // resolves against state.risks, exactly as FindingDetailModal resolves it.
  const riskIds = new Set((state.risks || []).filter(Boolean).map((r) => r.id));
  for (const f of state.findings || []) {
    if (!f || f.owner !== userId || f.status !== 'accepted-risk') continue;
    if ((f.linkedRiskIds || []).some((id) => riskIds.has(id))) continue;
    push({
      id: `finding-acceptance-unlinked-${f.id}`,
      kind: 'finding-acceptance-unlinked',
      bucket: 'decision',
      subject: { module: 'findings', id: f.id, version: null },
      criticality: { severity: f.severity ?? null },
      nav: { kind: 'route', path: '/findings' },
      record: f,
    });
  }

  // ── Tasks (Tier 1/2/3, the one task list) ──
  const commQuestionTasks = commQuestionTaskViews(extras.commQuestions, userId);
  for (const t of sortTasks(
    listMyTasks(
      state,
      userId,
      isAdmin,
      extras.obligationTasks,
      commQuestionTasks,
      extras.workflowSteps,
      riskAiAssessmentTasks(extras.riskAiAssessments || [], users),
      extras.haltReviews
    ),
    now
  )) {
    push({
      id: `task-${t.id}`,
      kind: t._source === 'adhoc' ? 'task' : t._source,
      bucket: isPast(t.dueDate, now) ? 'overdue' : 'ontrack',
      subject: { module: taskSubjectModule(t), id: t.id, version: null },
      dueDate: t.dueDate || null,
      // A question asked about a mandatory item carries that item's mandate
      // for its whole life (ACEA-07): the notice and this list say the same.
      ...(t._source === 'comm-question'
        ? { mandatory: { decisionOwed: t.mandatory === true } }
        : {}),
      nav: { kind: 'modal', modal: taskNav(t) },
      record: t,
      detail: { unassigned: t._unassigned === true },
    });
  }

  return out;
}

module.exports = {
  ITEM_KINDS,
  itemKindSpec,
  mandatoryFlagsFor,
  CATEGORY_ORDER,
  classifyRiskForUser,
  listVendorsNotResponding,
  listVendorsPendingArchiveProposal,
  listMyActionItems,
};
